package com.evoapps.evoshift.documents

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.DocumentProblem
import com.evoapps.evoshift.domain.model.InvalidDocumentException
import com.evoapps.evoshift.domain.model.OpenableDocument
import com.evoapps.evoshift.domain.model.PresentationFiles
import com.evoapps.evoshift.domain.usecase.documents.PickedFile

/**
 * The platform's file choosers for documents (SH-122): Android's document picker
 * (ACTION_OPEN_DOCUMENT, no storage permission), iOS's document picker (Files, a copy of the
 * picked file), the browser's file input, the desktop's Open dialog. A picked file arrives with
 * its name and at most [PICK_READ_LIMIT] bytes (a larger file is then refused as too large
 * without being read whole); closing a chooser sends nothing.
 */
interface DocumentPicker {
    /** Opens the chooser for files of [kind]. */
    fun pick(kind: PickKind)

    /** Opens the chooser for a PDF only ([pdfOnly], policies) or a PDF, JPEG or PNG. */
    fun pick(pdfOnly: Boolean) = pick(if (pdfOnly) PickKind.PDF else PickKind.DOCUMENT)
}

/** What a chooser offers. */
enum class PickKind {
    /** A PDF (policies, a presentation's PDF export). */
    PDF,
    /** A PDF, JPEG or PNG (people's documents, course pictures). */
    DOCUMENT,
    /** SH-211: a presentation: PowerPoint .pptx, Impress .odp or a PDF. */
    PRESENTATION
}

/** Called with the picked file, or with null when it could not be read. */
typealias OnDocumentPicked = (PickedFile?) -> Unit

@Composable
internal expect fun rememberPlatformDocumentPicker(onPicked: OnDocumentPicked): DocumentPicker

/** Replaces the platform choosers where there is no system UI (UI tests). */
val LocalDocumentPickerOverride = staticCompositionLocalOf<((OnDocumentPicked) -> DocumentPicker)?> { null }

/** The chooser for this platform, or the [LocalDocumentPickerOverride] when one is provided. */
@Composable
fun rememberDocumentPicker(onPicked: OnDocumentPicked): DocumentPicker {
    val override = LocalDocumentPickerOverride.current
    return override?.invoke(onPicked) ?: rememberPlatformDocumentPicker(onPicked)
}

/** One byte over the limit is enough to know a file is too large. */
internal const val PICK_READ_LIMIT = DocumentFiles.MAX_BYTES + 1

/** How much of a picked file of [kind] is read (one byte over its limit). */
internal fun pickReadLimit(kind: PickKind): Int =
    if (kind == PickKind.PRESENTATION) PresentationFiles.MAX_BYTES + 1 else PICK_READ_LIMIT

/** The MIME types a chooser offers. */
internal fun pickMimeTypes(kind: PickKind): List<String> = when (kind) {
    PickKind.PDF -> listOf(DocumentFiles.PDF)
    PickKind.DOCUMENT -> DocumentFiles.STAFF_TYPES.toList()
    PickKind.PRESENTATION -> PresentationFiles.TYPES.toList()
}

/** The file name extensions a chooser offers (desktop, browser). */
internal fun pickExtensions(kind: PickKind): List<String> = when (kind) {
    PickKind.PDF -> listOf("pdf")
    PickKind.DOCUMENT -> listOf("pdf", "jpg", "jpeg", "png")
    PickKind.PRESENTATION -> listOf("pptx", "odp", "pdf")
}

/** SH-233: what a drop place for files of [kind] takes: the chooser's types and read limit, one file. */
fun documentDropSpec(kind: PickKind): com.evoapps.evoshift.filedrop.FileDropSpec = com.evoapps.evoshift.filedrop.FileDropSpec(
    extensions = pickExtensions(kind).toSet(),
    readLimit = pickReadLimit(kind),
    mimeTypes = pickMimeTypes(kind).toSet()
)

/** A dropped file as the chooser hands it over (null: it could not be read). */
fun com.evoapps.evoshift.filedrop.DroppedFile?.asPickedFile(): PickedFile? = this?.let { PickedFile(it.name, it.bytes) }

/**
 * Opens a downloaded document for the person to read: Android's viewer of their choice, iOS's
 * share sheet (Quick Look, Files), a new browser tab (or a download), the desktop's default app.
 * Bound per platform in platformModule; UI tests bind a recorder.
 */
fun interface DocumentOpener {
    fun open(fileName: String, contentType: String, bytes: ByteArray)
}

/**
 * The name and type a platform opener writes and hands over (SH-177): from the bytes, never from
 * the stored name ([DocumentFiles.openable]). Not a PDF, JPEG or PNG: the "unsupported file" error
 * of the screen, nothing is written or opened.
 */
internal fun openableDocument(fileName: String, bytes: ByteArray): OpenableDocument =
    DocumentFiles.openable(fileName, bytes) ?: throw InvalidDocumentException(DocumentProblem.UNSUPPORTED_TYPE)
